<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue';
  import { useFormContext, useShared, useHttpClient, setModelWithContext, useRoute } from '@othercommerce/foundation';
  import type { WarehouseResource } from '@storefront/resources';

  defineOptions({
    inheritAttrs: false,
  });

  const route = useRoute();
  const http = useHttpClient();
  const context = useFormContext();
  const shared = useShared();

  const loading = ref(true);
  const points = ref([] as WarehouseResource[]);
  const hideWarning = ref(false);

  const current = computed(() => {
    if (context) {
      return context.data.value.shippingPointId;
    }
  });

  const shippingOptionId = computed(() => {
    if (context) {
      return context.data.value.shippingOptionId;
    }
  });

  const selected = computed(() => {
    return shared.value.currentPickupPoint;
  });

  function actionSelect(point: WarehouseResource) {
    hideWarning.value = false;

    setModelWithContext('shippingPointId', context, point.id);
    setModelWithContext('shippingPointName', context, point.name);
  }

  function actionVerify() {
    http.patch(route('web.cart.warehouse.update'), { warehouse: current.value, shippingOptionId: shippingOptionId.value });
  }

  onMounted(() => {
    http.get(route('web.cart.shipping.option.pickupPoints')).then((res) => {
      points.value = res.warehouses;
      loading.value = false;

      if (context) {
        if (!context.data.value.shippingPointId && points.value.length > 0) {
          actionSelect(selected.value || points.value[0]);
        }
      }
    });
  });
</script>
<template>
  <SfDataLoader :loading="loading">
    <div class="collect">
      <div class="collect__section">
        <div class="collect__header">
          <h3 class="collect__heading">
            {{ $t('SHOP.CART.HEADER.SHIPPING_SELECT_PICKUP_POINT') }}
          </h3>
        </div>
        <div class="collect__points">
          <SfListCard
            v-for="point in points" class="collect__point"
            :selected="current == point.id"
            shadow
            small
            interactive
            @click="actionSelect(point)"
            @keydown.space="actionSelect(point)"
            tabindex="0"
            role="button"
          >
            <template #default>
              <address class="collect__address">
                <p v-if="point.city" class="collect__point-city"><b>{{ point.city }}</b></p>
                <p v-if="point.name" class="collect__point-name">{{ point.name }}</p>
                <p v-if="point.firstLine" class="collect__point-first-line">{{ point.firstLine }}</p>
                <p v-if="point.secondLine" class="collect__point-second-line">{{ point.secondLine }}</p>
                <p v-if="point.thirdLine" class="collect__point-third-line">{{ point.thirdLine }}</p>
                <p v-if="point.contactEmail" class="collect__point-email">
                  <a :href="point.contactEmail">
                    {{ point.contactEmail }}
                  </a>
                </p>
                <p v-if="point.contactPhone" class="collect__point-phone">
                  <a :href="point.contactPhone.rfc">
                    {{ point.contactPhone.national }}
                  </a>
                </p>
              </address>
            </template>
          </SfListCard>
        </div>
      </div>

      <SfListCard v-if="!hideWarning && selected && current != selected.id" class="collect__warning">
        <template #default>
          <p v-html="$t('SHOP.CART.MESSAGE.SHIPPING_PICKUP_POINT_NOTICE')" />
        </template>
        <template #actions>
          <div class="collect__warning__actions">
            <SfButton primary small @click="actionVerify">
              Tak
            </SfButton>
            <SfButton small @click="hideWarning = true">
              Nie
            </SfButton>
          </div>
        </template>
      </SfListCard>
    </div>
  </SfDataLoader>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .collect {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    &__warning {
      --sf-list-card-color: var(--sf-status-color-severe);

      display: flex;
      flex-direction: column;
      font-size: 0.75rem;
      font-weight: var(--font-weight-semi);
      padding: 1rem;
      gap: 1rem;

      &__actions {
        display: flex;
        gap: 0.25rem;
      }
    }

    &__points {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 2rem;
    }

    &__form {
      display: flex;
      flex-direction: column;
    }

    &__address {
      font-style: normal;

      p {
        font-size: 0.875rem;
      }
    }

    &__point-name {
      margin-bottom: 0.5rem;
      font-size: 1rem !important;
    }

    &__section {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__form {
      display: flex;
      flex-direction: column;
      gap: var(--form-gap-y) var(--form-gap-x);
      max-width: 672px;
    }
  }

  @include media.query(lg) {
    .collect {
      &__points {
        gap: 1rem;
      }

      &__heading {
        font-size: 1.25rem;
      }
    }
  }

  @include media.query(md) {
    .collect__points {
      grid-template-columns: 1fr;
    }
  }
</style>
